import React, { ReactChild, Fragment } from "react";
import { ChangeEvent } from "react";
import { Prompt, RouteComponentProps } from "react-router-dom";

import { apiGet, apiPatch, apiPost } from "../../Api";
import withTitle from "../withTitle";

import ValidateInput from "../ValidateInput";

import "../../blocks.css";
import "../../form.css";
// import "../../indicator.css";
// import "../../list.css";
import "../../spinner.css";
// import "../../table.css";
import "../../user.css";
import "../viewer.css";

interface IRoleDetailsState {
    error?: string;
    isLoading: boolean;
    role?: any;

    name: string;

    isSubmitting: boolean;
    submitError?: string;
    hasChanged: boolean;

    permissions: IRolePermissionGroup[];

    [name: string]: any;
}

interface IRolePermissionGroup {
    name: string;
    children: IRolePermissionDef[];
}

interface IRolePermissionDef {
    id: string;
    name: string;
    description: string | ReactChild;
    rights: string[];
    rightsMatch?: string[];
}

class RoleDetailsView extends React.Component<RouteComponentProps<any>, IRoleDetailsState> {
    private inputs: ValidateInput[];

    constructor(props: Readonly<RouteComponentProps<any>>) {
        super(props);

        const state: IRoleDetailsState = {
            isLoading: true,

            name: "",

            hasChanged: false,
            isSubmitting: false,

            permissions: [
                {
                    name: "Dashboard",
                    children: [
                        {
                            id: "role_dashboard_stats",
                            name: "View stats widget",
                            description: "Allows users to see the dashboard's stats widget.",
                            rights: ["dash:stats"]
                        }
                    ]
                },
                {
                    name: "Task List",
                    children: [
                        {
                            id: "role_tasklist_view",
                            name: "View task list",
                            description: "Allows users to view their own tasks.",
                            rights: ["tasks:read"]
                        },
                        {
                            id: "role_tasklist_view_all",
                            name: "View everyone's task lists",
                            description: <Fragment>Allows users to view<em>everyone's</em> tasks.</Fragment>,
                            rights: ["tasks:read", "tasks:read:all"]
                        },
                        {
                            id: "role_tasklist_create",
                            name: "Create & edit tasks",
                            description: "Allows users to create and add notes to tasks.",
                            rights: ["tasks:create", "tasks:write"]
                        }
                    ]
                },
                {
                    name: "Files & Folders",
                    children: [
                        {
                            id: "role_folders_read",
                            name: "View files & folders",
                            description: "Allows users to view folders and the files contained within.",
                            rights: ["folders:read", "files:read"]
                        },
                    ]
                },
                {
                    name: "Health Fairs",
                    children: [
                        {
                            id: "role_fair_view",
                            name: "View health fairs",
                            description: "Allows users to view health fair information and the calendar.",
                            rights: ["fair:read", "employee:summary", "sched:mv:s", "sched:c:status"]
                        },
                        {
                            id: "role_fair_digital",
                            name: "Activate online health fairs",
                            description: "Allows users to activate and run online health fairs.",
                            rights: ["fair:d:act"]
                        },
                        {
                            id: "role_fair_create",
                            name: "Create & edit health fairs",
                            description: "Allows users to create and modify health fairs.  Also allows access to target company contact information.",
                            rights: ["fair:create", "fair:write", "employee:summary", "sched:mv:s"]
                        },
                        {
                            id: "role_fair_edit_admin",
                            name: "Edit health fairs after scheduled time",
                            description: "Allows users to edit health fair information after the scheduled time has passed",
                            rights: ["fair:write:admin"]
                        },
                        {
                            id: "role_fair_notices",
                            name: "Add/Edit health fair notices",
                            description: "Allows user to manage notices on the health fair calendar",
                            rights: ["fair:notice"]
                        },
                        {
                            id: "role_fair_delete",
                            name: "Delete health fairs",
                            description: <Fragment>Allows users to <em>permanently</em> delete health fairs</Fragment>,
                            rights: ["fair:delete"]
                        },
                        {
                            id: "role_fair_delete_own",
                            name: "Delete your own health fairs",
                            description: <Fragment>Allows users to <em>permanently</em> delete health fairs that they are assigned to</Fragment>,
                            rights: ["fair:delete:own"]
                        },
                        {
                            id: "role_fair_view_emails",
                            name: "View all email addresses from a health fair",
                            description: "Allows users to view all email addresses from a health fair.",
                            rights: ["fair:read:emails"]
                        },
                        {
                            id: "role_fair_view_template",
                            name: "View email template for a health fair",
                            description: "Allows users to view the email template for a health fair.",
                            rights: ["fair:read:temp"]
                        },
                        {
                            id: "role_fair_view_documents",
                            name: "View documents uploaded to a health fair",
                            description: "Allows users to view documents that have been uploaded into a health fair.",
                            rights: ["fair:doc:read"]
                        },
                        {
                            id: "role_fair_upload_documents",
                            name: "Upload documents into a health fair",
                            description: "Allows users to upload documents into a health fair.",
                            rights: ["fair:doc:read", "fair:doc:upload"]
                        },
                        {
                            id: "role_fair_delete_documents",
                            name: "Delete uploaded documents",
                            description: <Fragment>Allows users to <em>permanently</em> delete uploaded health fair documents.</Fragment>,
                            rights: ["fair:doc:delete"]
                        },
                        {
                            id: "role_tco_merge",
                            name: "Merge target companies",
                            description: "Allows users to merge 2 target companies into a single company",
                            rights: ["tco:merge"]
                        },
                        {
                            id: "role_tco_delete",
                            name: "Delete target companies",
                            description: <Fragment>Allows users to <em>permanently</em> delete target companies that have no fairs.</Fragment>,
                            rights: ["tco:delete"]
                        },
                    ]
                },
                {
                    name: "Health Fairs \u2014 Questionnaires",
                    children: [
                        {
                            id: "role_survey_view",
                            name: "View questionnaires",
                            description: "Allows users to view submitted questionnaires including submitter information and answers.",
                            rights: ["survey:read"]
                        },
                        {
                            id: "role_survey_digital_view",
                            name: "View digital surveys",
                            description: "Allows users to view submitted digital serveys including submitter information and answers.",
                            rights: ["survey:d:read"]
                        },
                        {
                            id: "role_survey_refer",
                            name: "Refer surveys to on-call",
                            description: "Allows users to refer surveys to on-call people",
                            rights: ["survey:refer"]
                        },
                        {
                            id: "role_survey_edit",
                            name: "Make corrections to questionnaires",
                            description: "Allows users to edit questionnaires for the purpose of making corrections.",
                            rights: ["survey:write", "survey:t:s"]
                        },
                        {
                            id: "role_survey_create",
                            name: "Manually create questionnaires",
                            description: "Allows users to manually create a questionnaire on behalf of someone. (i.e. create one based on a paper survey)",
                            rights: ["survey:create", "survey:t:s"]
                        },
                        {
                            id: "role_survey_delete",
                            name: "Delete questionnaires",
                            description: <Fragment>Allows users to <em>permanently</em> delete questionnaires</Fragment>,
                            rights: ["survey:delete"]
                        },
                        {
                            id: "role_survey_contact",
                            name: "View & Edit questionnaire outreach information",
                            description: "Allows updating questionnaire outreach status and notes",
                            rights: ["survey:contact", "survey:write"]
                        },
                        {
                            id: "role_survey_contact_extra_filter",
                            name: "Extra status filter options",
                            description: "Gives user extra quesionnaire outreach status filter options",
                            rights: ["survey:contact:exflt"]
                        },
                        {
                            id: "role_survey_contact_textproof",
                            name: "Change outreach status with only a text note",
                            description: "Allows updating questionnaire outreach status by supplying only a text note",
                            rights: ["survey:contact:tproof"]
                        },
                        {
                            id: "role_survey_contact_noproof",
                            name: "Change outreach status without uploading proof",
                            description: <Fragment>Allows updating questionnaire outreach status <em>without uploading proof</em></Fragment>,
                            rights: ["survey:contact:nproof"]
                        },
                    ]
                },
                {
                    name: "Health Fairs \u2014 Uploads",
                    children: [
                        {
                            id: "role_fair_photos_read",
                            name: "View uploaded social media photos",
                            description: "Allows users to view uploaded social media photos for health fairs.",
                            rights: ["fair:p:s:read"]
                        },
                        {
                            id: "role_fair_photos_upload",
                            name: "Upload social media photos",
                            description: "Allows users to upload social media photos for health fairs.",
                            rights: ["fair:p:s:upload"]
                        },
                        {
                            id: "role_fair_photos_delete",
                            name: "Delete social media photos",
                            description: <Fragment>Allows users to <em>permanently</em> delete social media photos from health fairs.</Fragment>,
                            rights: ["fair:p:s:delete"]
                        },
                        {
                            id: "role_fair_receipts_read",
                            name: "View uploaded receipts",
                            description: "Allows users to view uploaded receipts for health fairs.",
                            rights: ["fair:p:r:read"]
                        },
                        {
                            id: "role_fair_receipts_upload",
                            name: "Upload receipts",
                            description: "Allows users to upload receipts for health fairs.",
                            rights: ["fair:p:r:upload"]
                        },
                        {
                            id: "role_fair_receipts_delete",
                            name: "Delete receipts",
                            description: <Fragment>Allows users to <em>permanently</em> receipts from health fairs.</Fragment>,
                            rights: ["fair:p:r:delete"]
                        },
                    ]
                },
                {
                    name: "Outreach",
                    children: [
                        {
                            id: "role_surgical_consultant",
                            name: "Use Surgical Consultant Outreach & On-Call Facilities",
                            description: "Allows viewing and using the surgical consultant's outreach and on-call facilities",
                            rights: ["oreach:sc", "survey:contact"]
                        },
                        {
                            id: "role_patient_care_rep",
                            name: "Use Patient Care Representative Outreach & On-Call Facilities",
                            description: "Allows viewing and using the patient care representative's outreach and on-call facilities",
                            rights: ["oreach:tm", "survey:contact"]
                        },
                        {
                            id: "role_patient_care_rep_lead",
                            name: "Add Patient Care Representative Leads",
                            description: "Allows adding external leads to contact",
                            rights: ["oreach:tm:li"]
                        },
                        {
                            id: "role_patient_care_rep_self_lead",
                            name: "Add Your Own Patient Care Representative Leads",
                            description: "External leads added will be bound to the creator",
                            rights: ["oreach:tm:li", "oreach:tm:li:s"]
                        },
                        {
                            id: "role_surgical_consultant_lead",
                            name: "Add Surgical Consultant Leads",
                            description: <>Allows adding external leads for surgical consultants <em>as well as</em> patient care representatives</>,
                            rights: ["oreach:tm:li", "oreach:sc:li"]
                        },
                        {
                            id: "role_appointment_specialist",
                            name: "Use Appointment Specialist Outreach Facilities",
                            description: "Allows viewing and using the surgical specialist's outreach facilities",
                            rights: ["oreach:as", "survey:contact"]
                        },
                        {
                            id: "role_appointment_specialist_upload",
                            name: "Upload Annual Report for Appointment Specialist",
                            description: "Allows uploading the annual appointment report from Prime Suite to populate the possible past-due annual outreach list",
                            rights: ["oreach:as:an:upload"]
                        },
                        {
                            id: "role_contact_note_optional",
                            name: "Do Not Require Contact Note",
                            description: "Allows user to skip entering an outreach note to change status",
                            rights: ["oreach:c:note:opt"]
                        },
                        {
                            id: "role_email_outreach",
                            name: "Allow email outreach",
                            description: "Allows user to use the email outreach system",
                            rights: ["survey:email", "survey:read"]
                        },
                    ]
                },
                {
                    name: "Marketing Leads",
                    children: [
                        {
                            id: "role_lead_view",
                            name: "View marketing leads",
                            description: "Allows viewing marketing leads.",
                            rights: ["lead:read"]
                        },
                        {
                            id: "role_lead_edit",
                            name: "Create & edit marketing leads",
                            description: "Allows creating new marketing leads and editing existing leads.",
                            rights: ["lead:write", "lead:create"]
                        },
                        {
                            id: "role_lead_delete",
                            name: "Delete marketing leads",
                            description: <Fragment>Allows user to <em>permanently</em> delete marketing leads and lead notes.</Fragment>,
                            rights: ["lead:delete"]
                        }
                    ]
                },
                {
                    name: "Office Appointments",
                    children: [
                        {
                            id: "role_appt_view",
                            name: "View own appointments",
                            description: "View summary and detailed information about scheduled appointments the user has scheduled.",
                            rights: ["appt:read"]
                        },
                        {
                            id: "role_appt_view_all",
                            name: "View all appointments",
                            description: "View summary and detailed information about all scheduled appointments.",
                            rights: ["appt:read", "appt:readall", "employee:summary"]
                        },
                        {
                            id: "role_appt_create",
                            name: "Create appointments",
                            description: "Allows scheduling of appointments.",
                            rights: ["appt:create", "employee:summary", "sched:off:s", "sched:prov:s"]
                        },
                        {
                            id: "role_appt_edit",
                            name: "Edit appointments",
                            description: "Allows editing of appointments.",
                            rights: ["appt:write", "employee:summary"]
                        },
                        {
                            id: "role_appt_create_bare",
                            name: "Create appointments without questionnaires",
                            description: "Allows scheduling of appointments not linked to a health fair questionnaire.",
                            rights: ["appt:create", "appt:create:bare", "appt:write", "employee:summary"]
                        },
                        {
                            id: "role_appt_create_after_fair_no_proof",
                            name: "Create appointments from questionnaires without proof",
                            description: "Allows scheduling of appointments from a health fair questionnaire after the day of the fair without requiring an image as proof",
                            rights: ["appt:create:noproof"]
                        },
                        {
                            id: "role_appt_create_delegated",
                            name: "Assign appointments to people other than yourself",
                            description: "Allows changing who will show up as the one who scheduled the appointment",
                            rights: ["appt:create", "appt:create:delegated", "appt:write", "employee:summary"]
                        },
                        {
                            id: "role_appt_delete",
                            name: "Delete office appointments",
                            description: <Fragment>Allows user to <em>permanently</em> delete office appointments</Fragment>,
                            rights: ["appt:delete"]
                        },
                        // {
                        //    id: "role_stats_appointments",
                        //    name: "View appointment stats (beta)",
                        //    description: "Allows viewing appointment stats.",
                        //    rights: ["appt:stats:read"]
                        // },
                        {
                            id: "role_appt_insurance",
                            name: "View & edit insurance billing information",
                            description: "Allows viewing and updating of appointment insurance billing information",
                            rights: ["appt:read:ins"]
                        },
                        {
                            id: "role_appt_insurance_notes",
                            name: "View & add insurance notes",
                            description: "Allows seeing and adding insurance notes",
                            rights: ["appt:note:ins"]
                        },
                        {
                            id: "role_appt_note_delete",
                            name: "Delete appointment insurance notes",
                            description: <Fragment>Allows user to <em>permanently</em> delete notes</Fragment>,
                            rights: ["appt:delete:note"]
                        },
                        {
                            id: "role_appt_contact",
                            name: "View & Edit appointment outreach information",
                            description: "Allows updating office appointment outreach status and notes",
                            rights: ["appt:contact"]
                        },
                        {
                            id: "role_appt_email",
                            name: "Allow sending missed appointment emails",
                            description: "Allows sending templated emails about missed appointments",
                            rights: ["appt:email"]
                        },
                    ]
                },
                {
                    name: "Office Appointments \u2014 Pellets",
                    children: [
                        {
                            id: "role_consult_read",
                            name: "View & edit pellet consultation page",
                            description: "Allows viewing and using the pellet condultation page, including making notes",
                            rights: ["appt:con:read", "appt:con:write"]
                        },
                        {
                            id: "role_consult_delete",
                            name: "Delete pellet consultation entries",
                            description: <Fragment>Allows user to <em>permanently</em> delete pellet condulatation entries, and notes.</Fragment>,
                            rights: ["appt:con:delete"]
                        },
                    ]
                },
                {
                    name: "Telemedicine",
                    children: [
                        {
                            id: "role_telemed_read",
                            name: "View received telemedicine forms",
                            description: "Allows viewing all received telemedicine forms, for all locations",
                            rights: ["forms:read"]
                        },
                        {
                            id: "role_telemed_delete",
                            name: "Delete received telemedicine forms",
                            description: <Fragment>Allows user to <em>permanently</em> delete received telemedicine forms.</Fragment>,
                            rights: ["forms:delete"]
                        },
                    ]
                },
                {
                    name: "Surgery",
                    children: [
                        {
                            id: "role_surgery_view",
                            name: "View surgeries",
                            description: "View surgery list and details",
                            rights: ["surgery:read", "surgery:summary"]
                        },
                        {
                            id: "role_surgery_view_yours",
                            name: "View your surgeries",
                            description: <Fragment>View <em>Your Cases</em> page</Fragment>,
                            rights: ["surgery:yourcases", "surgery:summary", "bonus:read"]
                        },
                        {
                            id: "role_surgery_view_contact",
                            name: "View surgery contact list",
                            description: "View surgery contact list information",
                            rights: ["surgery:read", "surgery:contact"]
                        },
                        {
                            id: "role_surgery_patient_list",
                            name: "View & Use patient list / check-in",
                            description: "Allows viewing & using the surgical patient list and check-in system",
                            rights: ["surgery:patientlist"]
                        },
                        {
                            id: "role_surgery_edit_insurance",
                            name: "View & edit surgery insurance billing information",
                            description: "Allows viewing & updating of surgery case insurance billing information",
                            rights: ["surgery:read:ins", "surgery:write:ins"]
                        },
                        {
                            id: "role_surgery_review",
                            name: "View & use surgery review page",
                            description: "Allows reviewing surgeries",
                            rights: ["surgery:review"]
                        },
                        {
                            id: "role_surgery_view_history",
                            name: "View surgery history",
                            description: "Allows user to view the history of a surgery",
                            rights: ["surgery:history"]
                        },
                        {
                            id: "role_surgery_create",
                            name: "Create & edit surgeries - Basic",
                            description: "Create, edit, and schedule surgeries with basic information",
                            rights: ["surgery:create", "surgery:write", "employee:summary"]
                        },
                        {
                            id: "role_surgery_create_full",
                            name: "Create & edit surgeries - Full",
                            description: "Create, edit, and schedule surgeries with full information",
                            rights: ["surgery:create", "surgery:create:full", "surgery:write", "employee:summary"]
                        },
                        {
                            id: "role_surgery_create_marketer",
                            name: "Assign marketer & bonus type",
                            description: "Allows changing the marketer and bonus type associated with a surgery appointment",
                            rights: ["surgery:create:marketer"]
                        },
                        {
                            id: "role_surgery_view_m",
                            name: "View marketer-specific information",
                            description: "Allows viewing marketer-specific information (i.e. bonus type) associated with a surgery appointment",
                            rights: ["surgery:read:m"]
                        },
                        {
                            id: "role_surgery_edit_admin",
                            name: "Edit surgeries after finalization",
                            description: "Edit surgery appointments after finalizing (Complete/canceled/etc.)",
                            rights: ["surgery:write:admin"]
                        },
                        {
                            id: "role_surgery_cancel",
                            name: "Cancel surgeries",
                            description: "Allows soft and hard canceling of surgeries",
                            rights: ["surgery:cancel"]
                        },
                        {
                            id: "role_surgery_reschedule",
                            name: "Reschedule surgeries",
                            description: "Allows rescheduling the date & time of surgeries that already have a date and/or time set",
                            rights: ["surgery:resched"]
                        },
                        {
                            id: "role_surgery_reschedule_time",
                            name: "Change surgery scheduled time",
                            description: "Allows changing only the scheduled time of surgeries",
                            rights: ["surgery:resched:time"]
                        },
                        {
                            id: "role_surgery_status_reset",
                            name: "Reset surgery status",
                            description: "Allows resetting a surgery's status after it's been set to a final status (complete or canceled)",
                            rights: ["surgery:s:reset"]
                        },
                        {
                            id: "role_surgery_report_book",
                            name: "Generate surgery report (book)",
                            description: "Generate and print surgery list book",
                            rights: ["surgery:report"]
                        },
                        {
                            id: "role_surgery_notes_pre",
                            name: "Add pre-surgery notes",
                            description: "Allows adding pre-surgery notes on the surgery details page",
                            rights: ["surgery:note:pre"]
                        },
                        {
                            id: "role_surgery_notes_post",
                            name: "Add post-surgery notes",
                            description: "Allows adding post-surgery notes on the surgery details page",
                            rights: ["surgery:note:post"]
                        },
                        {
                            id: "role_surgery_notes_marketing",
                            name: "View & Add surgery marketing notes",
                            description: "Allows viewing and adding marketing notes on the surgery details page",
                            rights: ["surgery:note:marketing"]
                        },
                        {
                            id: "role_surgery_notes_insurance",
                            name: "View & Add surgery insurance notes",
                            description: "Allows viewing and adding insurance notes on the surgery details page",
                            rights: ["surgery:note:ins"]
                        },
                        {
                            id: "role_surgery_notes_contact",
                            name: "View & Add surgery contact notes",
                            description: "Allows viewing and adding contact notes on the surgery details page",
                            rights: ["surgery:note:contact"]
                        },
                        {
                            id: "role_surgery_notes_upload",
                            name: "Attach files to notes",
                            description: "Allows uploading and attaching files to notes, where applicable",
                            rights: ["surgery:note:upload"]
                        },
                        {
                            id: "role_surgery_delete",
                            name: "Delete surgeries",
                            description: <Fragment>Allows user to <em>permanently</em> delete surgeries and their notes</Fragment>,
                            rights: ["surgery:delete", "surgery:delete:note"]
                        },
                        {
                            id: "role_surgery_delete_note",
                            name: "Delete surgery notes",
                            description: <Fragment>Allows user to <em>permanently</em> delete notes from surgeries</Fragment>,
                            rights: ["surgery:delete:note"]
                        }
                    ]
                },
                {
                    name: "Surgery \u2014 Documents",
                    children: [
                        {
                            id: "role_surgery_doc_read",
                            name: "View surgery documents",
                            description: "Allows viewing of uploaded surgery documents",
                            rights: ["surgery:doc:read"]
                        },
                        {
                            id: "role_surgery_doc_upload",
                            name: "Upload surgery documents",
                            description: "Allows uploading of surgery documents",
                            rights: ["surgery:doc:upload"]
                        },
                        {
                            id: "role_surgery_doc_delete",
                            name: "Delete surgery documents",
                            description: <Fragment>Allows user to <em>permanently</em> delete surgery documents</Fragment>,
                            rights: ["surgery:doc:delete"]
                        },
                    ]
                },
                {
                    name: "Surgery \u2014 Printing",
                    children: [
                        {
                            id: "role_surgery_print_use",
                            name: "View & print documents",
                            description: "Allows printing of pre-prepared documents and PDFs for surgery",
                            rights: ["print:use", "print:surgery"]
                        },
                    ]
                },
                {
                    name: "Patient Charts",
                    children: [
                        {
                            id: "role_patient_view",
                            name: "View patient charts",
                            description: "Allows viewing basic information of a patient",
                            rights: ["patient:view"]
                        },
                        {
                            id: "role_patient_edit",
                            name: "Edit patient charts",
                            description: "Allows updating a patient's information",
                            rights: ["patient:view", "patient:edit"]
                        },
                    ]
                },
                {
                    name: "Patient Charts \u2014 Documents",
                    children: [
                        {
                            id: "role_documents_view",
                            name: "View chart documents",
                            description: "Allows read-only access to chart documents",
                            rights: ["document:read"]
                        },
                        {
                            id: "role_documents_edit",
                            name: "Edit chart documents",
                            description: "Allows editing chart documents",
                            rights: ["document:read", "document:edit"]
                        },
                        {
                            id: "role_documents_sign",
                            name: "Sign chart documents",
                            description: "Allows signing finalized chart documents",
                            rights: ["document:read", "document:sign"]
                        },
                        {
                            id: "role_documents_unlock",
                            name: "Unlock all chart documents",
                            description: "Allows unlocking any chart documents, including ones locked by other people",
                            rights: ["document:read", "document:unlock"]
                        },
                        {
                            id: "role_documents_delete",
                            name: "Delete chart documents",
                            description: <Fragment>Allows user to <em>permanently</em> delete chart documents</Fragment>,
                            rights: ["document:read", "document:delete"]
                        },
                        {
                            id: "role_documents_import_documents",
                            name: "Import Barcoded Documents",
                            description: "Allows batch importing barcoded patient documents",
                            rights: ["patient:view", "surgery:summary", "patient:doc:import"]
                        },
                        {
                            id: "role_documents_create_template",
                            name: "Create & delete chart document templates",
                            description: "Allows creating and deleting chart document templates",
                            rights: ["document:temp:new"]
                        },
                    ]
                },
                {
                    name: "Metrics",
                    children: [
                        {
                            id: "role_metrics_read",
                            name: "Read employee metrics",
                            description: "View employee metrics",
                            rights: ["metrics:read"]
                        },
                        {
                            id: "role_metrics_read_providers",
                            name: "View & update provider metrics",
                            description: "View and update provider daily case numbers",
                            rights: ["metrics:prov:read", "metrics:prov:write"]
                        },
                        {
                            id: "role_metrics_read_marketing",
                            name: "View marketing metrics",
                            description: "View marketing stats",
                            rights: ["metrics:mark:read"]
                        },
                        {
                            id: "role_metrics_read_location",
                            name: "View location metrics",
                            description: "View case stats by location",
                            rights: ["metrics:loc:read"]
                        },
                        {
                            id: "role_stats_cases",
                            name: "View case stats sheet",
                            description: "Allows viewing of the case stats sheet",
                            rights: ["metrics:cs:read"]
                        },
                        {
                            id: "role_stats_cases_write",
                            name: "Edit case stats settings",
                            description: "Allows editing of case stats sheet settings and goals",
                            rights: ["metrics:cs:write"]
                        },
                        {
                            id: "role_adhoc_wcas_emails",
                            name: "View WCAS survey email report",
                            description: "Allows viewing the WCAS survey email report",
                            rights: ["metrics:ah:wcas"]
                        },
                    ]
                },
                {
                    name: "Metrics \u2014 Analysis",
                    children: [
                        {
                            id: "role_analysis_read_ytd",
                            name: "View analysis graph",
                            description: "Allows viewing analysis graph",
                            rights: ["analysis:read:ytd"]
                        }
                    ]
                },
                {
                    name: "Metrics \u2014 Bonusing",
                    children: [
                        {
                            id: "role_metrics_bonus_read_self",
                            name: "View own bonuses",
                            description: "Allows viewing your own bonuses",
                            rights: ["bonus:own:read"]
                        },
                        {
                            id: "role_metrics_bonus_read_marketer",
                            name: "View marketing bonuses",
                            description: "Allows viewing bonuses for the marketers",
                            rights: ["metrics:bonus:m:read"]
                        },
                        {
                            id: "role_metrics_bonus_calc_marketer",
                            name: "Calculate marketing bonuses",
                            description: "Allows calculating bonuses for the marketers",
                            rights: ["metrics:bonus:m:calc"]
                        },
                        {
                            id: "role_metrics_bonus_read_np",
                            name: "View NP bonuses",
                            description: "Allows viewing bonuses for the NPs",
                            rights: ["metrics:bonus:n:read"]
                        },
                        {
                            id: "role_metrics_bonus_calc_np",
                            name: "Calculate NP bonuses",
                            description: "Allows calculating bonuses for the NPs",
                            rights: ["metrics:bonus:n:calc"]
                        },
                        {
                            id: "role_metrics_bonus_read_doctor",
                            name: "View doctor bonuses",
                            description: "Allows viewing bonuses for the doctors",
                            rights: ["metrics:bonus:d:read"]
                        },
                        {
                            id: "role_metrics_bonus_calc_doctor",
                            name: "Calculate doctor bonuses",
                            description: "Allows calculating bonuses for the doctors",
                            rights: ["metrics:bonus:d:calc"]
                        },
                        {
                            id: "role_metrics_bonus_read_surgical",
                            name: "View surgical outcomes bonuses",
                            description: "Allows viewing bonuses for surgical outcomes",
                            rights: ["metrics:bonus:so:read"]
                        },
                        {
                            id: "role_metrics_bonus_calc_surgical",
                            name: "Calculate surgical outcomes bonuses",
                            description: "Allows calculating bonuses for surgical outcomes",
                            rights: ["metrics:bonus:so:calc"]
                        },
                        {
                            id: "role_metrics_bonus_read_marketing_manager",
                            name: "View marketing manager bonuses",
                            description: "Allows viewing bonuses for the marketing manager",
                            rights: ["metrics:bonus:mm:read"]
                        },
                        {
                            id: "role_metrics_bonus_calc_marketing_manager",
                            name: "Calculate marketing manager bonuses",
                            description: "Allows calculating bonuses for the marketing manager",
                            rights: ["metrics:bonus:mm:calc"]
                        },
                    ]
                },
                {
                    name: "Metrics \u2014 Assessment",
                    children: [
                        {
                            id: "role_metrics_assessment_self",
                            name: "View own assessments",
                            description: "Allows viewing your own assessments, submitted by your supervisor",
                            rights: ["assess:read:self"]
                        },
                        {
                            id: "role_metrics_assessment_write",
                            name: "View & edit assessments of your subordinates",
                            description: "Allows viewing and updating assessments of your designated subordinates",
                            rights: ["assess:read", "assess:edit"]
                        },
                        {
                            id: "role_metrics_assessment_all",
                            name: "View all assessments",
                            description: "Allows viewing assessments for all employees",
                            rights: ["assess:read:all"]
                        },
                        {
                            id: "role_metrics_assessment_admin",
                            name: "Manage assessment types",
                            description: "Allows managing assessment types and their questions",
                            rights: ["assess:admin"]
                        },
                    ]
                },
                {
                    name: "Schedule",
                    children: [
                        {
                            id: "role_schedule_read",
                            name: "View own schedule",
                            description: "View user's own work schedule.",
                            rights: ["sched:read"]
                        },
                        {
                            id: "role_schedule_provider_read",
                            name: "View provider schedule",
                            description: "View provider's work schedule.",
                            rights: ["sched:prov:read", "sched:vac:s"]
                        },
                        {
                            id: "role_schedule_provider_write",
                            name: "Edit provider schedule",
                            description: "Edit provider's work schedule.",
                            rights: ["sched:prov:write"]
                        },
                        {
                            id: "role_schedule_office_read",
                            name: "View office schedule",
                            description: "View office hours schedule.",
                            rights: ["sched:off:read"]
                        },
                        {
                            id: "role_schedule_office_write",
                            name: "Edit office schedule",
                            description: "Edit office hours schedule.",
                            rights: ["sched:off:write"]
                        },
                        {
                            id: "role_schedule_co_read",
                            name: "View clinical outcomes schedule",
                            description: "View clinical outcomes schedule.",
                            rights: ["sched:co:read", "sched:vac:s"]
                        },
                        {
                            id: "role_schedule_co_write",
                            name: "Edit clinical outcomes schedule",
                            description: "Edit clinical outcomes schedule.",
                            rights: ["sched:co:write"]
                        },
                        {
                            id: "role_schedule_mv_read",
                            name: "View marketer vacation schedule",
                            description: "View marketer vacation calendar.",
                            rights: ["sched:mv:read"]
                        },
                        {
                            id: "role_schedule_mv_write",
                            name: "Edit marketer vacation schedule",
                            description: "Edit marketer vacation schedule.",
                            rights: ["sched:mv:write"]
                        },
                        {
                            id: "role_schedule_cov_read",
                            name: "View CO vacation schedule",
                            description: "View clinical outcomes manager's vacation calendar.",
                            rights: ["sched:cov:read"]
                        },
                        {
                            id: "role_schedule_cov_write",
                            name: "Edit CO vacation schedule",
                            description: "Edit clinical outcomes manager's vacation calendar.",
                            rights: ["sched:cov:write"]
                        },
                        {
                            id: "role_schedule_c_read",
                            name: "View coverage schedule",
                            description: "View coverage calendar.",
                            rights: ["sched:c:read", "sched:dc:read"]
                        },
                        {
                            id: "role_schedule_tm_read",
                            name: "View patient care rep. on-call schedule",
                            description: "View patient care rep. on-call calendar.",
                            rights: ["sched:c:read", "sched:tm:read"]
                        },
                        {
                            id: "role_schedule_sc_read",
                            name: "View surgical consultant on-call schedule",
                            description: "View surgical consultant on-call calendar.",
                            rights: ["sched:c:read", "sched:sc:read"]
                        },
                        {
                            id: "role_schedule_c_assign",
                            name: "Assign people on coverage schedules",
                            description: "Allows assigning people to slots on coverage schedules.",
                            rights: ["sched:c:assign"]
                        },
                        {
                            id: "role_schedule_c_create",
                            name: "Create coverages",
                            description: "Allows creating new coverages.",
                            rights: ["sched:c:create"]
                        },
                        {
                            id: "role_schedule_c_write",
                            name: "Edit coverages",
                            description: "Allows editing coverage information (like name, slots, and active ranges) on coverage schedules.",
                            rights: ["sched:c:write"]
                        },
                        {
                            id: "role_schedule_c_delete",
                            name: "Delete coverages",
                            description: <Fragment>Allows <em>permanently</em> deleting coverages.</Fragment>,
                            rights: ["sched:c:delete"]
                        },
                    ]
                },
                {
                    name: "Files",
                    children: [
                        {
                            id: "role_files_read",
                            name: "Read uploaded files",
                            description: "Allows reading/downloading of uploaded files.",
                            rights: ["files:read"]
                        },
                        {
                            id: "role_files_upload",
                            name: "Upload files",
                            description: "Allows uploading files.",
                            rights: ["files:upload"]
                        },
                        {
                            id: "role_files_admin",
                            name: "View file list & delete files",
                            description: "Allows viewing the uploaded file list and deleting files.",
                            rights: ["files:admin"]
                        },
                    ]
                },
                {
                    name: "Accounting",
                    children: [
                        {
                            id: "role_accounting_receipt_read",
                            name: "View receipts",
                            description: "Allows viewing uploaded receipts",
                            rights: ["acct:recpt:read", "files:read"]
                        },
                        {
                            id: "role_accounting_statements_read",
                            name: "View statements",
                            description: "Allows viewing billing statements",
                            rights: ["stmt:read", "stmt:acct:read"]
                        },
                        {
                            id: "role_accounting_statements_write",
                            name: "Update statement status",
                            description: "Allows updating billing statements' status",
                            rights: ["stmt:write"]
                        },
                        {
                            id: "role_accounting_statement_account_write",
                            name: "Update statement account information",
                            description: "Allows updating billing statements' account information (like opting someout out of receiving emailed statements)",
                            rights: ["stmt:acct:write"]
                        },
                        {
                            id: "role_accounting_statements_read_metrics",
                            name: "View statement metrics",
                            description: "Allows viewing billing statement metrics",
                            rights: ["stmt:metric:read"]
                        },
                        {
                            id: "role_accounting_statements_print",
                            name: "Print paper statements",
                            description: "Allows controlling the batch printing of paper statements",
                            rights: ["stmt:print"]
                        },
                    ]
                },
                {
                    name: "Accounting \u2014 Insurance Companies",
                    children: [
                        {
                            id: "role_accounting_insurance_read",
                            name: "View insurance companies",
                            description: "Allows viewing of insurance company list",
                            rights: ["acct:ins:read"]
                        },
                        {
                            id: "role_accounting_insurance_create",
                            name: "Create & edit insurance companies",
                            description: "Allows creating and editing insurance companies",
                            rights: ["acct:ins:create", "acct:ins:write"]
                        },
                        {
                            id: "role_accounting_insurance_delete",
                            name: "Delete insurance companies",
                            description: <Fragment>Allows <em>permanently</em> deleting insurance companies</Fragment>,
                            rights: ["acct:ins:delete"]
                        },
                    ]
                },
                {
                    name: "Reminders & Messaging",
                    children: [
                        {
                            id: "role_reminders_view",
                            name: "View appointment reminders",
                            description: "Allows viewing appointment reminders",
                            rights: ["remind:read"]
                        },
                        {
                            id: "role_reminders_send",
                            name: "Send ad-hoc appointment reminder messages",
                            description: "Allows sending ad-hoc reminder messages for upcoming appointments, like for severe weather impacting availability and safety",
                            rights: ["remind:read", "remind:send"]
                        },
                        {
                            id: "role_messaging_read",
                            name: "Read messages",
                            description: "Allows reading message threads",
                            rights: ["msg:read"]
                        },
                        {
                            id: "role_messaging_send",
                            name: "Send messages",
                            description: "Allows sending text messages",
                            rights: ["msg:read", "msg:send"]
                        },
                        {
                            id: "role_messaging_new",
                            name: "Create new conversations",
                            description: "Allows creating new conversations and sending text messages to numbers not from reminders",
                            rights: ["msg:read", "msg:send", "msg:thread:new"]
                        },
                        {
                            id: "role_reminder_settings",
                            name: "View & update reminder and messaging settings",
                            description: "Allows managing reminder and messaging settings, including reminder scheduling",
                            rights: ["msg:admin"]
                        },
                        {
                            id: "role_messaging_number_settings",
                            name: "Manage messaging numbers",
                            description: "Allows managing numbers used for messaging, and their assignments",
                            rights: ["msg:num:admin"]
                        },
                    ]
                },
                {
                    name: "Tools",
                    children: [
                        {
                            id: "role_tool_obflow",
                            name: "Use OB Flowsheet Converter",
                            description: "Use the OB Lab Flowsheet converter",
                            rights: ["tool:obflow"]
                        },
                    ]
                },
                {
                    name: "Vehicles",
                    children: [
                        {
                            id: "role_vehicle_summary",
                            name: "View vehicle list",
                            description: "Allows viewing the vehicle list.",
                            rights: ["veh:summary"]
                        },
                        {
                            id: "role_vehicle_view",
                            name: "Create & Edit vehicles",
                            description: "Allows creating and editing vehicles.",
                            rights: ["veh:summary", "veh:read", "veh:write", "veh:create"]
                        },
                        {
                            id: "role_vehicle_tracker_view",
                            name: "View tracking information",
                            description: "Allows viewing the current location and mileage of vehicles.",
                            rights: ["veh:tracker:read"]
                        },
                        {
                            id: "role_vehicle_tracker_edit",
                            name: "Link trackers",
                            description: "Allows linking trackers to vehicles.",
                            rights: ["veh:tracker:edit"]
                        },
                        {
                            id: "role_vehicle_note_create",
                            name: "Create vehicle notes",
                            description: "Allows creating new vehicle notes.",
                            rights: ["veh:note:create"]
                        },
                        {
                            id: "role_vehicle_note_delete",
                            name: "Delete vehicle notes",
                            description: <Fragment>Allows <em>permanently</em> deleting vehicle notes.</Fragment>,
                            rights: ["veh:note:delete"]
                        },
                        {
                            id: "role_vehicle_delete",
                            name: "Delete vehicles",
                            description: <Fragment>Allows <em>permanently</em> deleting vehicles.</Fragment>,
                            rights: ["veh:delete"]
                        },
                    ]
                },
                {
                    name: "Administration",
                    children: [
                        {
                            id: "role_role_edit",
                            name: "Manage roles",
                            description: "View and edit permission roles.",
                            rights: ["role:read", "role:write", "role:create", "role:delete"]
                        },
                        {
                            id: "role_user_edit",
                            name: "Manage users",
                            description: "View and edit user accounts.",
                            rights: ["user:read", "user:write", "user:create", "user:delete", "role:read"]
                        },
                        {
                            id: "role_employee_edit",
                            name: "Manage employees",
                            description: "View and edit employees.",
                            rights: ["employee:read", "employee:summary", "employee:write", "employee:create", "employee:delete", "location:read"]
                        },
                        {
                            id: "role_location_edit",
                            name: "Manage locations",
                            description: "View and edit Howard Center locations.",
                            rights: ["location:read", "location:write", "location:create", "location:delete"]
                        },
                        {
                            id: "role_device_manage",
                            name: "Manage devices",
                            description: "Manage external devices",
                            rights: ["device:read", "device:write", "device:create", "device:delete"]
                        },
                        {
                            id: "role_surgery_admin",
                            name: "Manage surgery settings",
                            description: "Manage surgery types, devices, specimens, etc. used by surgeries",
                            rights: ["surgery:admin"]
                        },
                        {
                            id: "role_bonus_admin",
                            name: "Manage bonus settings",
                            description: "Manage marketer bonusing settings",
                            rights: ["bonus:admin"]
                        },
                        {
                            id: "role_survey_admin",
                            name: "Manage survey types",
                            description: "Manage questionnaire types and the questions in them",
                            rights: ["survey:t:admin"]
                        },
                        {
                            id: "role_appt_type_admin",
                            name: "Manage office appointment types",
                            description: "Manage office appointment types",
                            rights: ["appt:t:admin"]
                        },
                        {
                            id: "role_list_admin",
                            name: "Manage data lists",
                            description: "Manage all data lists",
                            rights: ["list:admin"]
                        },
                    ]
                },
                {
                    name: "Audit Log",
                    children: [
                        {
                            id: "role_auditlog_view",
                            name: "View audit log",
                            description: "Read-only access to the audit log",
                            rights: ["auditlog:read", "employee:summary", "user:summary"]
                        },
                    ]
                }
            ]
        };

        for (const group of state.permissions) {
            for (const permission of group.children) {
                state[permission.id] = false;
            }
        }

        this.state = state;

        this.submitChanges = this.submitChanges.bind(this);
        this.cancelChanges = this.cancelChanges.bind(this);
        this.handleInputChanged = this.handleInputChanged.bind(this);
        this.handleRef = this.handleRef.bind(this);

        this.inputs = [];
    }

    public async componentDidMount() {
        const userId = (this.props.match && this.props.match.params.id);

        try {
            if (userId) {
                // Load existing role
                const role = await apiGet<any>(`roles/${userId}`);

                const newState: Partial<IRoleDetailsState> = {
                    isLoading: false,
                    role,

                    name: role.name
                };

                const rightsMap: { [name: string]: boolean } = {};
                for (const right of role.permissions) {
                    rightsMap[right] = true;
                }

                // Enable role permissions based on existing rights
                for (const group of this.state.permissions) {
                    for (const permission of group.children) {
                        const rightList = permission.rightsMatch || permission.rights;

                        let allChecked = true;
                        for (const right of rightList) {
                            allChecked = rightsMap[right] && allChecked;
                        }

                        if (allChecked) {
                            newState[permission.id] = true;
                        }
                    }
                }

                this.setState(newState as any);
            }
            else {
                this.setState({
                    isLoading: false
                });
            }
        }
        catch (e) {
            this.setState({
                error: e.message,
                isLoading: false
            });
        }
    }

    public render() {
        if (this.state.isLoading) {
            return (
                <div className="viewer-root">
                    <div className="spinner-center lds-dual-ring-big" />
                </div>
            );
        }

        if (this.state.error) {
            return (
                <div className="viewer-root">
                    <span className="error-text">{this.state.error}</span>
                </div>
            );
        }

        return (
            <div className="viewer-root with-actionbar input-form">
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <div className="column-root">
                    <section className="info-block">
                        <header>
                            <h3>Role Information</h3>
                        </header>
                        <section>
                            <div className="input-form-group">
                                <label htmlFor="frm-role-name">Name</label>
                                <ValidateInput type="text" id="frm-role-name" ref={this.handleRef} className="input-light" name="name" value={this.state.name} onChange={this.handleInputChanged} placeholder="Name" autoComplete="off_please_chrome" isRequired={true} />
                            </div>
                        </section>
                    </section>
                </div>
                &nbsp;
                <section className="info-block">
                    <header>
                        <h3>Permissions</h3>
                    </header>

                    {this.state.permissions.map(a => this.renderGroup(a))}
                </section>
                &nbsp;
                {this.state.submitError ? <span className="error-text">{this.state.submitError}</span> : ""}
                <div className="action-panel">
                    <button className="button blue btn-confirm push-right" disabled={this.state.isSubmitting} onClick={this.submitChanges}>Save</button>
                    <button className="button blue btn-cancel" disabled={this.state.isSubmitting} onClick={this.cancelChanges}>Cancel</button>
                </div>
            </div>
        );
    }

    private async submitChanges() {
        let allValid = true;
        for (const input of this.inputs) {
            allValid = input.validate() && allValid;
        }

        if (!allValid) {
            return;
        }

        const rightsMap: { [name: string]: boolean } = {};

        // Build list of rights
        for (const group of this.state.permissions) {
            for (const permission of group.children) {
                if (this.state[permission.id] === true) {
                    for (const right of permission.rights) {
                        rightsMap[right] = true;
                    }
                }
            }
        }

        const rights = Object.keys(rightsMap);

        try {
            if (this.state.role) {
                // Update existing role
                this.setState({
                    isSubmitting: true,
                    submitError: undefined
                });

                const update = {
                    name: this.state.name,
                    permissions: rights,
                };

                await apiPatch(`roles/${this.state.role.id}`, update);

                this.setState({
                    hasChanged: false,
                    isSubmitting: false,
                });
            }
            else {
                // Create new role
                const creationInfo = {
                    name: this.state.name,
                    permissions: rights,
                };

                this.setState({
                    isSubmitting: true,
                    submitError: undefined
                });

                await apiPost("roles", creationInfo);

                this.setState({
                    hasChanged: false,
                    isSubmitting: false,
                });
            }
            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                isSubmitting: false,
                submitError: e.message
            });
        }
    }

    private cancelChanges() {
        this.props.history.goBack();
    }

    private handleInputChanged(e: ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.type === 'checkbox' ? e.currentTarget.checked : e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            hasChanged: true,
            [name]: value
        } as any)
    }

    private handleRef(input: ValidateInput) {
        this.inputs.push(input);
    }

    private renderGroup(group: IRolePermissionGroup) {
        return <Fragment key={group.name}>
            <h4>{group.name}</h4>
            {group.children.map(a => this.renderRole(a))}
        </Fragment>
    }

    private renderRole(role: IRolePermissionDef) {
        return (
            <section key={role.id} className="setting-section">
                <input type="checkbox" id={role.id} name={role.id} checked={(this.state as any)[role.id]} onChange={this.handleInputChanged} />
                <div className="section-label-group">
                    <label htmlFor={role.id}>
                        <span>{role.name}</span>
                        <small>{role.description}</small>
                    </label>
                </div>
            </section>
        );
    }
}

export default withTitle("Role Details", RoleDetailsView);
